import * as DocumentPicker from 'expo-document-picker';
import { DocumentResult } from 'expo-document-picker';
import React, { useRef } from 'react';
import { toast } from 'react-toastify';

import { auth0Audience } from '../../api/API';
import { Environment } from '../../Environment';
import { Auth0Context, useAuth0 } from '../Auth0Provider';
import { ButtonState } from '../MNOHSButton';

type Files = {
  students: DocumentResult | undefined;
};

export type StudentsUploadProps = {
  // inputs
  /** files picked */
  files: Files;
  /** button state */
  buttonState: ButtonState;

  // outputs
  /** invoked if user wants to upload a csv file */
  userChoseDocument: () => void;
  /** invoked if user clicks "Process" button */
  userClickedProcess: () => void;
};

export const studentsUploadContainer = (Screen: React.ComponentType<StudentsUploadProps>) => () => {
  const [files, setFiles] = React.useState<Files>({ students: undefined });
  const [buttonState, setButtonState] = React.useState<ButtonState>('enabled');

  const isMounted = useRef(true);
  const { getTokenSilently } = useAuth0() as Auth0Context;
  const { api } = Environment.current();

  // To prevent update on unmount
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <Screen
      files={files}
      buttonState={files.students ? buttonState : 'disabled'}
      userChoseDocument={async () => {
        setButtonState('showingActivityIndicator');
        const pickedFile = await DocumentPicker.getDocumentAsync({ type: 'text/csv' });
        if (pickedFile.type !== 'success') {
          setButtonState('enabled');
          return;
        }

        if (isMounted.current) {
          setFiles({ ...files, students: pickedFile });
          setButtonState('enabled');
        }
      }}
      userClickedProcess={async () => {
        try {
          if (files.students?.type !== 'success') return;
          if (!files.students.file) return;

          setButtonState('showingActivityIndicator');
          const token = await getTokenSilently({
            audience: auth0Audience,
            scope: 'admin:students',
          });

          const result = await api.bulkUploadStudents(files.students.file, token || '');
          if (isMounted.current) {
            toast.success(`Successfully sent bulk upload request! Status - ${result.status}`);
            setButtonState('enabled');
            setFiles({ students: undefined });
          }
        } catch (e) {
          toast.error(e.message);
          setButtonState('enabled');
        }
      }}
    />
  );
};
